/* -------------------------------------------------------------------------- */
/*                              Timezone Utilities                            */
/* -------------------------------------------------------------------------- */

import moment from 'moment-timezone';
import { store } from '@src/redux/storeConfig/store';
import * as authSelectors from '@redux/reducers/auth';

/**
 * Obtiene el timezone configurado en el perfil del usuario
 * @returns {string} Nombre del timezone del usuario o timezone del navegador como fallback
 */
export const getUserTimezone = (): string => {
  const state = store.getState();
  return authSelectors.getAuthUserTimezoneName(state);
};

/**
 * Parsea una fecha que viene del servidor
 * El servidor envía fechas en formato ISO sin timezone (ej: "2025-10-29T17:26:03")
 * JavaScript las interpreta como hora local, pero necesitamos tratarlas como UTC
 * @param {Date | string} date - Fecha del servidor  
 * @returns {moment.Moment} Objeto moment en timezone del usuario
 */
const parseServerDate = (date: Date | string): moment.Moment => {
  const userTimezone = getUserTimezone();
  
  if (typeof date === 'string') {
    // CLAVE: Forzar que el string se trate como UTC añadiendo 'Z'
    // Si el string no termina en 'Z', lo agregamos para forzar UTC parsing
    const isoStringWithZ = date.endsWith('Z') ? date : date + 'Z';
    return moment.utc(isoStringWithZ).tz(userTimezone);
  }
  
  if (date instanceof Date) {
    // Si es Date, JavaScript ya lo interpretó como hora local
    // Extraer los valores raw y tratarlos como UTC
    const utcMoment = moment.utc([
      date.getFullYear(),
      date.getMonth(),
      date.getDate(), 
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    ]);
    
    return utcMoment.tz(userTimezone);
  }
  
  // Fallback
  return moment.tz(date, userTimezone);
};

/**
 * Formatea una fecha aplicando el timezone del usuario
 * @param {Date | string} date - Fecha a formatear del servidor
 * @param {string} format - Formato de salida (formato de moment.js)
 * @returns {string} Fecha formateada en el timezone del usuario
 */
export const formatDateInUserTimezone = (
  date: Date | string, 
  format: string = 'DD/MM/YYYY HH:mm:ss'
): string => {
  // Parseamos la fecha del servidor en el timezone del usuario
  const serverDate = parseServerDate(date);
  return serverDate.format(format);
};

/**
 * Formatea una fecha para mostrar solo la hora en el timezone del usuario
 * @param {Date | string} date - Fecha a formatear
 * @returns {string} Hora formateada (HH:mm)
 */
export const formatTimeInUserTimezone = (date: Date | string): string => {
  return formatDateInUserTimezone(date, 'HH:mm');
};

/**
 * Formatea una fecha completa en el timezone del usuario
 * @param {Date | string} date - Fecha a formatear
 * @returns {string} Fecha y hora formateadas (DD/MM/YYYY HH:mm:ss)
 */
export const formatDateTimeInUserTimezone = (date: Date | string): string => {
  return formatDateInUserTimezone(date, 'DD/MM/YYYY HH:mm:ss');
};

/**
 * Formatea una fecha para nombres de archivo aplicando el timezone del usuario
 * @param {Date | string} date - Fecha a formatear
 * @returns {string} Fecha formateada para nombres de archivo (YYYYMMDD_HHmmss)
 */
export const formatDateTimeForFileNameInUserTimezone = (date: Date | string): string => {
  return formatDateInUserTimezone(date, 'YYYYMMDD_HHmmss');
};

/**
 * Convierte una fecha al timezone del usuario manteniendo el objeto moment
 * @param {Date | string} date - Fecha a convertir del servidor
 * @returns {moment.Moment} Objeto moment en el timezone del usuario
 */
export const convertToUserTimezone = (date: Date | string): moment.Moment => {
  // Parseamos la fecha del servidor en el timezone del usuario
  return parseServerDate(date);
};

/**
 * Hook personalizado para usar con React components que necesiten timezone del usuario
 * @returns {object} Objeto con funciones de formateo y el timezone del usuario
 */
export const useUserTimezone = () => {
  const userTimezone = getUserTimezone();
  
  return {
    userTimezone,
    formatDate: (date: Date | string, format?: string) => formatDateInUserTimezone(date, format),
    formatTime: (date: Date | string) => formatTimeInUserTimezone(date),
    formatDateTime: (date: Date | string) => formatDateTimeInUserTimezone(date),
    convertToUserTimezone: (date: Date | string) => convertToUserTimezone(date)
  };
};

// Función de debugging - Activa temporalmente
export const debugTimezone = (date: Date | string, label: string = 'Date') => {
  const userTimezone = getUserTimezone();
  const originalMoment = moment(date);
  const parsedDate = parseServerDate(date);
  
  console.group(`🕐 ${label} Timezone Debug`);
  console.log('📅 Original:', date);
  console.log('🌍 User Timezone:', userTimezone);
  console.log('📋 Original as moment:', originalMoment.format('YYYY-MM-DD HH:mm:ss Z'));
  console.log('🔧 Treating as UTC values:', originalMoment.format('YYYY-MM-DD HH:mm:ss') + ' → UTC');
  console.log('🔄 Converted to user TZ:', parsedDate.format('YYYY-MM-DD HH:mm:ss Z'));
  console.log('⏰ Final formatted time:', parsedDate.format('HH:mm'));
  console.log('⚖️ Time difference:', originalMoment.format('HH:mm') + ' → ' + parsedDate.format('HH:mm'));
  console.groupEnd();
  
  return {
    original: date,
    userTimezone,
    originalMoment: originalMoment.format('YYYY-MM-DD HH:mm:ss Z'),
    parsedUserTz: parsedDate.format('YYYY-MM-DD HH:mm:ss Z'),
    finalTime: parsedDate.format('HH:mm'),
    timeDifference: originalMoment.format('HH:mm') + ' → ' + parsedDate.format('HH:mm')
  };
};
